import { ReactComponent as CalenderSmall } from "assets/calender_icon_small.svg";
import { ReactComponent as MailIcon } from "assets/mail_send_icon.svg";
import {
  functionalAreaIdToName,
  functionalAreaIdsToNames,
  getMoreValues,
  getTableColor,
  mapUserListToTooltip
} from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";

import { Box, Divider, LinearProgress } from "@mui/material";
import Typography from "components/common/Typography";
import moment from "moment";
import { tokens } from "styles/Tokens";
import { progressTypes } from "utils/CommonConstants";
import { getUIFormatDate } from "utils/DateHelper";
import { planProgressHeaders, propertiesProgressHeaders, userPlanProgressHeaders } from "./TableHeaders";

const getDateColor = (date: any, status: string) => {
  const todaysDate = moment().format("YYYY-MM-DD");
  return moment(date).isBefore(todaysDate) && status !== "Completed"
    ? tokens.Text.Feedback.Alert
    : tokens.Text.General.PrimaryHeadline;
};

export const getAssigneeOrOwnerList = (list: any, allusers: any) => {
  return list
    ?.map((hltId: any) => {
      const user = allusers.length && allusers?.find((item: any) => item.hltId === hltId);
      const displayName = getUserNameWithConditionalCheck(user?.displayName, user?.firstName, user?.lastName);
      return displayName;
    })
    .filter((entry: any) => entry);
};

export const getPlanProgressData = (tableData: any, allUsers: any, navigate: any) => {
  return {
    headers: planProgressHeaders,
    rows: tableData?.map((item: any) => {
      const progressVal = item?.planCompletedCardsCount / item?.planTotalCardsCount;
      return {
        data: [
          {
            content: (
              <Box
                onClick={() => item?.myPlanId && navigate(`/myplandetails/${item?.myPlanId}`)}
                className="cursor-pointer"
              >
                {item.innCode}
              </Box>
            ),
            showBold: true
          },
          {
            content: getMoreValues(getAssigneeOrOwnerList(item.hltIds, allUsers)),
            tooltip: { list: mapUserListToTooltip(getAssigneeOrOwnerList(item.hltIds, allUsers)) }
          },
          {
            content: getMoreValues(getAssigneeOrOwnerList(item.owners, allUsers)),
            tooltip: { list: mapUserListToTooltip(getAssigneeOrOwnerList(item.owners, allUsers)) }
          },
          { content: item.planStatus, color: getTableColor(item?.planStatus) },
          {
            content: (
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start"
                  }}
                >
                  <LinearProgress
                    sx={{ width: "80px", height: "2px" }}
                    variant="determinate"
                    value={!isNaN(progressVal) ? progressVal * 100 : 0}
                  />
                  <Typography variant="label" color={tokens.Text.General.Tertiary}>
                    {" "}
                    {`${item?.planCompletedCardsCount?.toString()}/${item?.planTotalCardsCount?.toString()} done`}
                  </Typography>
                </Box>
              </Box>
            )
          },
          { content: item?.engagement },
          { content: item.planPriority },

          {
            content: getUIFormatDate(item.planDueDate),
            color: getDateColor(item.planDueDate, item?.planStatus)
          },
          { content: getUIFormatDate(item.planCompletionDate) }
        ]
      };
    })
  };
};

export const getpropertyProgressData = (tableData: any, allUsers: any, navigate: any, functionalAreaList: any) => {
  return {
    headers: propertiesProgressHeaders,
    rows: tableData?.map((item: any) => {
      const progressVal = item?.planCompletedCardsCount / item?.planTotalCardsCount;

      return {
        data: [
          {
            content: (
              <Box
                onClick={() => item?.myPlanId && navigate(`/myplandetails/${item?.myPlanId}`)}
                className="cursor-pointer"
              >
                {item.myPlanName}
              </Box>
            ),
            showBold: true
          },
          {
            content: getMoreValues(getAssigneeOrOwnerList(item.hltIds, allUsers)),
            tooltip: { list: mapUserListToTooltip(getAssigneeOrOwnerList(item.hltIds, allUsers)) }
          },
          {
            content: getMoreValues(getAssigneeOrOwnerList(item.owners, allUsers)),
            tooltip: { list: mapUserListToTooltip(getAssigneeOrOwnerList(item.owners, allUsers)) }
          },
          {
            content: getMoreValues(functionalAreaIdsToNames(item.functionalAreaIds)),
            tooltip: {
              title: "Functional Areas",
              list: item.functionalAreaIds?.map((FA: any, index: number) => {
                return { name: functionalAreaIdToName(FA, functionalAreaList) };
              })
            }
          },
          {
            content: getUIFormatDate(item.planDueDate),
            color: getDateColor(item.planDueDate, item?.planStatus)
          },
          {
            content: (
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start"
                  }}
                >
                  <Typography showBold variant="label" color={tokens.Text.General.Tertiary}>
                    {item?.planStatus}
                  </Typography>
                  <LinearProgress
                    sx={{ width: "80px", height: "2px" }}
                    variant="determinate"
                    value={!isNaN(progressVal) ? progressVal * 100 : 0}
                  />
                  <Typography variant="label" color={tokens.Text.General.Tertiary}>
                    {" "}
                    {`${item?.planCompletedCardsCount?.toString()}/${item?.planTotalCardsCount?.toString()} done`}
                  </Typography>
                </Box>
              </Box>
            )
          },
          { content: getUIFormatDate(item.planCompletionDate) }
        ]
      };
    })
  };
};

export const getUserPlanProgressData = (tableData: any, allUsers: any, navigate: any, functionalAreaList: any) => {
  return {
    headers: userPlanProgressHeaders,
    rows: tableData?.map((item: any) => {
      const progressVal = item?.planCompletedCardsCount / item?.planTotalCardsCount;

      return {
        data: [
          {
            content: (
              <Box
                onClick={() => item?.myPlanId && navigate(`/myplandetails/${item?.myPlanId}`)}
                className="cursor-pointer"
              >
                {item.myPlanName}
              </Box>
            ),
            showBold: true
          },

          {
            content: getMoreValues(functionalAreaIdsToNames(item.functionalAreaIds)),

            tooltip: {
              title: "Functional Areas",
              list: item.functionalAreaIds?.map((FA: any, index: number) => {
                return { name: functionalAreaIdToName(FA, functionalAreaList) };
              })
            }
          },
          {
            content: getUIFormatDate(item.planDueDate),
            color: getDateColor(item.planDueDate, item?.planStatus)
          },
          {
            content: (
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start"
                  }}
                >
                  <Typography showBold variant="label" color={tokens.Text.General.Tertiary}>
                    {item?.planStatus}
                  </Typography>
                  <LinearProgress
                    sx={{ width: "80px", height: "2px" }}
                    variant="determinate"
                    value={!isNaN(progressVal) ? progressVal * 100 : 0}
                  />
                  <Typography variant="label" color={tokens.Text.General.Tertiary}>
                    {" "}
                    {`${item?.planCompletedCardsCount?.toString()}/${item?.planTotalCardsCount?.toString()} done`}
                  </Typography>
                </Box>
              </Box>
            )
          },
          {
            content: getUIFormatDate(item?.planCompletionDate)
          }
        ]
      };
    })
  };
};

export const getAssignmentData = (progressType: string, summaryDetail: any) => {
  const planAssignment =
    progressType === progressTypes.user ? summaryDetail?.totalPlansCount : summaryDetail?.implementedPlansCount;
  const lastAssignment =
    progressType === progressTypes.user ? summaryDetail?.lastAssignmentDateTime : summaryDetail?.lastAssignmentDate;

  return (
    <div
      // style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      className="left-wrapper-data-info-property-user"
    >
      {planAssignment && (
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "4px" }}>
          {" "}
          <MailIcon />
          <Typography variant={"body2"} color={tokens.Text.General.Secondary}>
            {" "}
            Plan Assignment:
          </Typography>
          <Typography variant={"body2"} showBold color={tokens.Text.General.Secondary}>
            {planAssignment} plans
          </Typography>
        </Box>
      )}
      {lastAssignment && (
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "4px" }}>
          {" "}
          <Divider className="property-user-divider" orientation="vertical"></Divider>
          <CalenderSmall />
          <Typography variant={"body2"} color={tokens.Text.General.Secondary}>
            {" "}
            Last Assignment:
          </Typography>
          <Typography variant={"body2"} showBold color={tokens.Text.General.Secondary}>
            {getUIFormatDate(lastAssignment)}
          </Typography>
        </Box>
      )}
    </div>
  );
};
